import { TenantConfig, WorkingHours } from "../types";

// Helper to check if a specific date is a valid working day
export function isDateWorkingDay(dateStr: string, config: TenantConfig): boolean {
    // Robust parsing: handle ISO strings by taking the date part
    const isoDate = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;
    const [year, month, day] = isoDate.split('-').map(Number);

    // Create local date for day-of-week check
    const dateObj = new Date(year, month - 1, day);
    const dayOfWeek = dateObj.getDay();

    const now = new Date();
    const isToday = now.getFullYear() === year &&
        now.getMonth() === (month - 1) &&
        now.getDate() === day;

    // If it's today, we must rely on the full open/closed logic (including time and manual switch)
    if (isToday) {
        return isRestaurantOpen(config);
    }

    // Prevent scheduling for past days
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    if (dateObj < todayStart) return false;

    // For future days: Check if there are configured hours
    // We rely on working_hours as the source of truth for "scheduled availability"
    // ignoring open_days here to avoid issues if that array is not perfectly synced with hours

    // JS getDay(): 0=Sun, 1=Mon, ..., 6=Sat
    // Backend (assumed Python/Django): 0=Mon, ..., 6=Sun
    const backendDayIndex = (dayOfWeek + 6) % 7;
    const dayHours = config.working_hours[backendDayIndex as keyof WorkingHours];

    // Check if there are configured hours for this day
    if (!dayHours || dayHours.length === 0) return false;

    return true;
}

export function isRestaurantOpen(config: TenantConfig): boolean {
    if (config.is_closed) return false;

    const now = new Date();
    const dayOfWeek = now.getDay(); // 0 is Sunday, 1 is Monday, ..., 6 is Saturday

    // JS getDay(): 0=Sun, 1=Mon, ..., 6=Sat
    // Backend (assumed Python/Django): 0=Mon, ..., 6=Sun
    const backendDayIndex = (dayOfWeek + 6) % 7;
    const todayHours = config.working_hours[backendDayIndex as keyof WorkingHours];

    if (!todayHours || todayHours.length === 0) return false;

    const currentTime = now.getHours() * 60 + now.getMinutes();

    return todayHours.some(range => {
        const [startH, startM] = range.start.split(':').map(Number);
        const [endH, endM] = range.end.split(':').map(Number);

        const startTime = startH * 60 + startM;
        const endTime = endH * 60 + endM;

        return currentTime >= startTime && currentTime <= endTime;
    });
}

export function getAvailableTimeSlots(dateStr: string, config: TenantConfig | null, intervalMinutes: number = 30): string[] {
    if (!config) return [];

    // Validar fecha
    const isoDate = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;
    const [year, month, day] = isoDate.split('-').map(Number);
    const dateObj = new Date(year, month - 1, day);

    const dayOfWeek = dateObj.getDay();
    // Backend (0=Mon, ..., 6=Sun) vs JS (0=Sun, ..., 6=Sat)
    const backendDayIndex = (dayOfWeek + 6) % 7;

    // @ts-ignore
    const dayHours = config.working_hours[backendDayIndex];

    if (!dayHours || dayHours.length === 0) return [];

    const slots: string[] = [];

    // Si es hoy, filtrar horarios pasados (+ buffer de preparación si se desea, por ahora solo "ahora")
    const now = new Date();
    const isToday = now.getFullYear() === year &&
        now.getMonth() === (month - 1) &&
        now.getDate() === day;

    const currentMinutes = now.getHours() * 60 + now.getMinutes();
    // Buffer minimo para pedidos de hoy: 30 mins
    const minTimeToday = currentMinutes + 30;

    dayHours.forEach((range: any) => {
        const [startH, startM] = range.start.split(':').map(Number);
        const [endH, endM] = range.end.split(':').map(Number);

        let startTotal = startH * 60 + startM;
        const endTotal = endH * 60 + endM;

        // Generar slots
        while (startTotal < endTotal) {
            // Si es hoy, verificar que el slot sea mayor al horario actual + buffer
            if (!isToday || startTotal >= minTimeToday) {
                const h = Math.floor(startTotal / 60);
                const m = startTotal % 60;
                const timeStr = `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
                slots.push(timeStr);
            }
            startTotal += intervalMinutes;
        }
    });

    return slots.sort();
}
